import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "@/components/LocalizedLink";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Moon, Sun } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { fetchSettingsContent } from "@/lib/contentService";
import LanguageSwitch from "@/components/LanguageSwitch";
import { cn } from "@/lib/utils";
import { getLogicalPath } from "@/lib/localeRouting";

/** Matches the `xl` breakpoint the desktop nav switches on. */
const DESKTOP_NAV_MEDIA_QUERY = '(min-width: 1280px)';

const Navbar = () => {
  const { lang, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const activePath = getLogicalPath(location.pathname);

  const { data: settings } = useQuery({
    queryKey: ["singleton", "settings"],
    queryFn: fetchSettingsContent,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // Widening past the breakpoint hides the panel via CSS, which would otherwise
  // leave the body scroll-locked with no way to reopen and dismiss it.
  useEffect(() => {
    if (!mobileOpen) return;

    const query = window.matchMedia(DESKTOP_NAV_MEDIA_QUERY);
    const close = () => setMobileOpen(false);

    query.addEventListener("change", close);
    return () => query.removeEventListener("change", close);
  }, [mobileOpen]);

  const navItems = useMemo(() => {
    if (settings?.navbarItems?.length) {
      return settings.navbarItems.map((item) => ({ label: item.label, path: item.href }));
    }

    return [
      { label: t("nav.services"), path: "/services" },
      { label: t("nav.solutions"), path: "/solutions" },
      { label: t("nav.projects"), path: "/projects" },
      { label: t("nav.tools"), path: "/tools" },
      { label: t("nav.blog"), path: "/blog" },
      { label: t("nav.about"), path: "/about" },
      { label: t("nav.contact"), path: "/contact" },
    ];
  }, [settings, t]);

  const ctaLabel = settings?.ctaPrimaryText || t("nav.cta");
  const ctaLink = settings?.ctaPrimaryUrl || "/contact";
  const isExternalCta = ctaLink.startsWith("http");

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
          scrolled || mobileOpen ? "bg-background/85 backdrop-blur-xl border-b border-border/50" : "",
        )}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 shrink-0 bg-foreground rounded-full" />
              <span className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold tracking-tight text-foreground">figtures</span>
                <span className="font-body text-[7px] tracking-[0.3em] text-muted-foreground uppercase">
                  product studio
                </span>
              </span>
            </Link>

            <div className="hidden xl:flex items-center gap-8">
              {navItems.slice(0, -1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "relative font-body text-sm transition-colors duration-300",
                    activePath === item.path
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {item.label}
                </Link>
              ))}
              <LanguageSwitch />
              <button
                onClick={toggleTheme}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={lang === "tr" ? "Tema değiştir" : "Toggle theme"}
              >
                {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>

            <div className="hidden xl:block">
              {isExternalCta ? (
                <a
                  href={ctaLink}
                  target="_blank"
                  rel="noreferrer"
                  className="font-body text-sm px-5 py-2 bg-foreground text-background rounded-full font-medium hover:opacity-90 transition-opacity"
                >
                  {ctaLabel}
                </a>
              ) : (
                <Link
                  to={ctaLink}
                  className="font-body text-sm px-5 py-2 bg-foreground text-background rounded-full font-medium hover:opacity-90 transition-opacity"
                >
                  {ctaLabel}
                </Link>
              )}
            </div>

            <div className="xl:hidden flex items-center gap-2.5">
              <LanguageSwitch compact />
              <button
                type="button"
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-transparent hover:border-border/60 transition-colors"
                onClick={() => setMobileOpen((open) => !open)}
                aria-label={
                  mobileOpen
                    ? (lang === "tr" ? "Menüyü kapat" : "Close menu")
                    : (lang === "tr" ? "Menüyü aç" : "Open menu")
                }
                aria-expanded={mobileOpen}
              >
                <span
                  className={cn(
                    "absolute block h-0.5 w-5 bg-foreground transition-all duration-300 ease-out",
                    mobileOpen ? "translate-y-0 rotate-45" : "-translate-y-[6px]",
                  )}
                />
                <span
                  className={cn(
                    "absolute block h-0.5 w-5 bg-foreground transition-all duration-300 ease-out",
                    mobileOpen ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100",
                  )}
                />
                <span
                  className={cn(
                    "absolute block h-0.5 w-5 bg-foreground transition-all duration-300 ease-out",
                    mobileOpen ? "translate-y-0 -rotate-45" : "translate-y-[6px]",
                  )}
                />
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.button
              type="button"
              aria-label={lang === "tr" ? "Menüyü kapat" : "Close menu"}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="fixed inset-0 z-40 bg-background/35 backdrop-blur-[2px] xl:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-x-0 top-16 bottom-0 z-40 xl:hidden bg-background/95 backdrop-blur-xl border-t border-border/40"
            >
              <div className="flex h-full flex-col px-6 pb-8 pt-6">
                <nav className="flex-1 overflow-y-auto overscroll-contain">
                  <ul className="divide-y divide-border/40">
                    {navItems.map((item) => (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className={cn(
                            "flex min-h-[3.5rem] items-center font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] transition-colors duration-200",
                            activePath === item.path
                              ? "text-foreground"
                              : "text-foreground/85 hover:text-foreground",
                          )}
                        >
                          <span className="py-4">{item.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="mt-6 flex items-center justify-between border-t border-border/50 pt-5">
                  <span className="font-body text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                    {lang === "tr" ? "Görünüm" : "Appearance"}
                  </span>
                  <button
                    type="button"
                    onClick={toggleTheme}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:text-foreground hover:border-border"
                    aria-label={lang === "tr" ? "Tema değiştir" : "Toggle theme"}
                  >
                    {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
